import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Container,
  Paper,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  CANCEL_BUTTON_TEXT,
  SHOW_NOTIFICATION,
  SUBMIT_BUTTON_TEXT,
} from '../../../../constants';
import { useDispatch } from 'react-redux';
import {
  getLeaveTypeForUser,
  getUsers,
} from '../../../../services/UserService';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { ERROR_KEY } from '../../../../constants';
import { applyLeaveQuota } from '../../../Services/EmployeePrivilegesService';
import { isCasualLeaveName } from '../../../../utils/casualLeaveUtils';

const ApplyLeavesQuota = (props) => {
  const { toggleOpen, refresh, setRefresh } = props;
  const [search, setSearch] = useState(false);
  const [toSubjects, setToSubjects] = useState([]);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loadingApiCall, setLoadingApiCall] = useState(false);
  const [employeeIdError, setEmployeeIdError] = useState('');
  const [leaveIdError, setLeaveIdError] = useState('');
  const [noOfDaysError, setNoOfDaysError] = useState('');
  const [leaveQuota, setLeaveQuota] = useState({
    employeeId: '',
    leaveId: '',
    noOfDays: '',
  });
  const [selectedLeaveGroupId, setSelectedLeaveGroupId] = useState(null);
  const [leaveGroupHydrationFailed, setLeaveGroupHydrationFailed] =
    useState(false);
  const [labels, setLabels] = useState([]);
  const [leaveTypeFlag, setLeaveTypeFlag] = useState(false);
  const dispatch = useDispatch();
  const date = new Date();

  const dipatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  const getLeaveTypesLabels = async (leaveGroupId, hydrationFailed) => {
    const response = await getLeaveTypeForUser(date.getFullYear());
    if (response.isError === false) {
      // Hide Casual Leave for grouped employees (annual quota is not editable).
      // Also hide when Leave Group hydration failed — do not treat as "No Group".
      // Backend remains authoritative for mutation rejection.
      const hasLeaveGroup = leaveGroupId != null && leaveGroupId !== '';
      const treatAsGrouped = hasLeaveGroup || hydrationFailed === true;
      const leaveTypes = treatAsGrouped
        ? response.data.filter((label) => !isCasualLeaveName(label.leaveName))
        : response.data;
      setLabels(leaveTypes);
      setLeaveTypeFlag(true);
    } else {
      setLeaveTypeFlag(false);
    }
    if (response.status !== 200) {
      dipatchNotification(response.type, response.message);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const subjects = await getUsers('');
    if (subjects.status !== 200) {
      dipatchNotification(subjects.type, subjects.message);
    }
    if (subjects.isError === false) {
      setToSubjects(() => subjects.data.content);
    }
  };

  useEffect(() => {
    if (leaveQuota.employeeId !== '') {
      getLeaveTypesLabels(selectedLeaveGroupId, leaveGroupHydrationFailed);
    }
  }, [leaveQuota.employeeId, selectedLeaveGroupId, leaveGroupHydrationFailed]);

  const handleSubmit = async () => {
    // Reset errors before validation
    setEmployeeIdError('');
    setLeaveIdError('');
    setNoOfDaysError('');

    // Validation for Employee ID
    if (leaveQuota.employeeId === '') {
      setEmployeeIdError('Employee ID is required');
    }

    // Validation for Leave ID
    if (leaveQuota.leaveId === '') {
      setLeaveIdError('Leave ID is required');
    }

    // Validation for No of Days
    if (!validateLeaveCount()) {
      setNoOfDaysError('Invalid leave count');
    }

    // If there are no errors, proceed with the submission
    if (
      leaveQuota.employeeId !== '' &&
      leaveQuota.leaveId !== '' &&
      validateLeaveCount() &&
      leaveQuota.noOfDays !== ''
    ) {
      console.log(leaveQuota, 'leaveQuota');
      const response = await applyLeaveQuota(leaveQuota);
      setLoadingApiCall(true);
      if (response.isError === false) {
        setLoadingApiCall(false);
        setRefresh(!refresh);
        toggleOpen();
        dipatchNotification(response.type, response.message);
      } else {
        setLoadingApiCall(false);
        dipatchNotification(response.type, response.message);
      }
    }
  };
  const validateLeaveCount = () => {
    if (leaveQuota.noOfDays === '') return false;
    else if (parseInt(leaveQuota.noOfDays) >= 0) {
      return true;
    } else return false;
  };

  return (
    <Container disableGutters>
      <Paper component={Box} elevation={0} p={5} pt={0}>
        <Grid container>
          <Grid
            container
            className="space-between-container"
            pt={4}
            sx={{
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: '2',
            }}
          >
            <Grid item>
              <IconButton sx={{ mt: '0px', ml: '-8px' }} onClick={toggleOpen}>
                <KeyboardBackspaceIcon />
              </IconButton>
            </Grid>
          </Grid>
          {
            <Grid container sx={{ mt: '15px', mb: '20px' }}>
              <Grid item xs={12} sx={{ mb: '15px' }}>
                <Autocomplete
                  forcePopupIcon={false}
                  disableClearable
                  open={search}
                  isOptionEqualToValue={(option, value) =>
                    option.employeeId === value.employeeId
                  }
                  options={toSubjects.map((option) => ({
                    employeeName: option.firstName + ' ' + option.lastName + ' (' + option.employeeCode + ')',
                    employeeId: option.id,
                    leaveGroupId: option.leaveGroupId,
                    leaveGroupHydrationFailed: option.leaveGroupHydrationFailed,
                  }))}
                  getOptionLabel={(option) => option.employeeName || ''}
                  onInputChange={(e, value) => {
                    setSearch(value.trim() !== '');
                    if (value.trim() === '') {
                      setEmployeeIdError('Employee name must not be empty.');
                    } else {
                      setEmployeeIdError('');
                    }
                  }}
                  onChange={(e, val) => {
                    if (!val || !val.employeeName.trim()) {
                      setEmployeeIdError('Please select a valid employee.');
                    } else {
                      setLeaveQuota({
                        ...leaveQuota,
                        employeeId: val.employeeId,
                        leaveId: '',
                      });
                      setSelectedLeaveGroupId(val.leaveGroupId ?? null);
                      setLeaveGroupHydrationFailed(
                        val.leaveGroupHydrationFailed === true,
                      );
                      setEmployeeIdError('');
                      setSearch(false);
                      setFirstSubmit(false);
                    }
                  }}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee Name / ID"
                      placeholder="Employee Name / ID"
                      error={employeeIdError !== ''}
                      helperText={employeeIdError}
                    />
                  )}
                  sx={{ mt: 2 }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: leaveTypeFlag ? 'block' : 'none' }}
              >
                <Grid container>
                  <Grid item xs={4}>
                    <FormControl
                      size="small"
                      fullWidth
                      error={firstSubmit && leaveQuota.leaveId === ''}
                    >
                      <InputLabel id="month-range-label">Leave Type</InputLabel>
                      <Select
                        labelId="month-range-label"
                        label="Leave Type"
                        value={leaveQuota.leaveId ? leaveQuota.leaveId : ''}
                        error={leaveIdError !== ''}
                        helperText={leaveIdError}
                        onChange={(event) => {
                          setLeaveQuota({
                            ...leaveQuota,
                            leaveId: event.target.value,
                          });
                          setLeaveIdError('');
                        }}
                      >
                        {labels.map((label, index) => (
                          <MenuItem key={index} value={label.id}>
                            {label.leaveName}
                          </MenuItem>
                        ))}
                      </Select>
                      {firstSubmit && leaveQuota.leaveId === '' && (
                        <FormHelperText>Select Leave Type</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} sx={{ ml: 2 }}>
                    <TextField
                      name="name"
                      size="small"
                      value={leaveQuota.noOfDays ? leaveQuota.noOfDays : ''}
                      error={
                        (leaveQuota.noOfDays === '' || !validateLeaveCount()) &&
                        noOfDaysError !== ''
                      }
                      helperText={noOfDaysError !== '' ? noOfDaysError : ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const sanitizedValue = inputValue.replace(/\D/g, '');
                        if (
                          sanitizedValue === '' ||
                          /^\d+$/.test(sanitizedValue)
                        ) {
                          setLeaveQuota({
                            ...leaveQuota,
                            noOfDays: sanitizedValue,
                          });
                          setNoOfDaysError('');
                        }
                      }}
                      label="Allot leaveQuota"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          }
          <Grid
            container
            className="space-between-container"
            sx={{ mt: '25px' }}
          >
            <Grid item>
              <Button onClick={toggleOpen} variant="outlined" color={ERROR_KEY}>
                {CANCEL_BUTTON_TEXT}
              </Button>
            </Grid>
            <Grid item>
              <LoadingButton
                onClick={handleSubmit}
                loading={loadingApiCall}
                variant="contained"
              >
                {loadingApiCall ? 'loading...' : SUBMIT_BUTTON_TEXT}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ApplyLeavesQuota;
